window.InfiniteScroll =
  window.InfiniteScroll ||
  class extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.isLoading = false;
    this.nextUrl = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Hide existing pagination
    const paginationWrapper = this.querySelector('.pagination-wrapper');
    if (paginationWrapper) {
      paginationWrapper.style.display = 'none';
    }

    // Initialize nextUrl from the current DOM
    const nextLink = this.querySelector('[data-pagination-next]') || this.querySelector('.pagination__item--prev');

    if (nextLink && nextLink.href) {
      this.nextUrl = nextLink.href;
    } else {
      return; // No next page
    }

    // Create sentinel
    if (!this.sentinel) {
      this.sentinel = document.createElement('div');
      this.sentinel.classList.add('infinite-scroll-sentinel');
      this.sentinel.style.height = '50px';
      this.sentinel.style.width = '100%';
      this.sentinel.style.marginTop = '20px';
      this.sentinel.innerHTML = `
        <div class="loading-spinner hidden">
          <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      `;
      this.appendChild(this.sentinel);
    }

    // Add styles for spinner
    if (!document.getElementById('infinite-scroll-styles')) {
      const style = document.createElement('style');
      style.id = 'infinite-scroll-styles';
      style.innerHTML = `
        .infinite-scroll-sentinel .loading-spinner {
          display: flex;
          justify-content: center;
          padding: 20px 0;
        }
        .infinite-scroll-sentinel .loading-spinner.hidden {
          display: none;
        }
        .infinite-scroll-sentinel .spinner {
          width: 30px;
          height: 30px;
          animation: rotator 1.4s linear infinite;
        }
        .infinite-scroll-sentinel .path {
          stroke-dasharray: 280;
          stroke-dashoffset: 0;
          transform-origin: center;
          stroke: rgb(var(--color-foreground));
          animation: dash 1.4s ease-in-out infinite;
        }
        @keyframes rotator {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(270deg); }
        }
        @keyframes dash {
          0% { stroke-dashoffset: 280; }
          50% { stroke-dashoffset: 75; transform: rotate(135deg); }
          100% { stroke-dashoffset: 280; transform: rotate(450deg); }
        }
      `;
      document.head.appendChild(style);
    }

    this.initObserver();
  }

  initObserver() {
    if (this.observer) this.observer.disconnect();

    const options = {
      root: null,
      rootMargin: '0px 0px 400px 0px', // Trigger early
      threshold: 0,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoading && this.nextUrl) {
          this.loadNextPage();
        }
      });
    }, options);

    this.observer.observe(this.sentinel);
  }

  async loadNextPage() {
    if (this.isLoading || !this.nextUrl) return;
    this.isLoading = true;

    // Show spinner
    const spinner = this.sentinel.querySelector('.loading-spinner');
    if (spinner) spinner.classList.remove('hidden');

    try {
      const response = await fetch(this.nextUrl);
      if (!response.ok) throw new Error('Network response was not ok');

      const text = await response.text();
      const parser = new DOMParser();
      const html = parser.parseFromString(text, 'text/html');

      // 1. Append new products
      const newProducts = html.querySelectorAll('#product-grid .grid__item');
      const currentProductGrid = document.getElementById('product-grid');

      if (newProducts.length && currentProductGrid) {
        newProducts.forEach((product) => {
          // Add animation class if needed
          product.classList.add('scroll-trigger', 'animate--slide-in');
          currentProductGrid.appendChild(product);
        });
      }

      // 2. Find the NEXT link in the new content
      // We look for the link that points to the page AFTER the one we just loaded
      const nextLink = html.querySelector('[data-pagination-next]') || html.querySelector('.pagination__item--prev');

      if (nextLink && nextLink.getAttribute('href')) {
        this.nextUrl = nextLink.getAttribute('href');
      } else {
        this.nextUrl = null;
        this.cleanup();
      }
    } catch (error) {
      console.error('Error loading next page:', error);
    } finally {
      // Small delay to allow DOM to update and observer to reset
      setTimeout(() => {
        this.isLoading = false;
        const spinner = this.sentinel.querySelector('.loading-spinner');
        if (spinner) spinner.classList.add('hidden');

        // If we still have a next URL and the sentinel is still visible (e.g. large screen),
        // the observer might need a nudge or the user didn't need to scroll.
        // We can manually check intersection or just let the observer handle it on next move.
        // But for robustness, we can check if we should load more immediately.
        if (this.nextUrl && this.isSentinelVisible()) {
          // Optional: recursively load if still visible?
          // Better to let the observer handle it, but we might need to re-observe.
        }
      }, 500);
    }
  }

  isSentinelVisible() {
    const rect = this.sentinel.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + 400 && // Account for rootMargin
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  cleanup() {
    if (this.observer) this.observer.disconnect();
    if (this.sentinel) this.sentinel.remove();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
  };

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', window.InfiniteScroll);
}
